<template>
  <div class="wrapper">
    <CRow class="mt-3">
      <CCol sm="12">
        <div v-if="isLoading" class="card">
          <div class="card-body text-center py-5">Loading...</div>
        </div>

        <div v-else-if="loadError" class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong>Join Request</strong>
            <CButton color="secondary" size="sm" @click="goBack">Back</CButton>
          </div>
          <div class="card-body text-center text-muted py-5">{{ loadError }}</div>
        </div>

        <template v-else>
          <!-- SUMMARY -->
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <strong>Join Request Summary</strong>
              <CButton color="secondary" size="sm" @click="goBack">Back</CButton>
            </div>
            <div class="card-body">
              <CAlert v-if="request.is_latest === false" color="info" class="py-2 small">
                Showing this person's latest request. Older requests are listed in Log Journey.
              </CAlert>
              <div class="d-flex align-items-center flex-wrap mb-3">
                <img v-if="people.url_photo" :src="people.url_photo" class="join-photo mr-3" alt="photo">
                <div v-else class="join-photo join-photo-initials mr-3">{{ initials }}</div>
                <div>
                  <h5 class="mb-1">{{ people.fullname || '-' }}</h5>
                  <CBadge :color="peopleStatusColor(people.status)" class="mr-1 text-capitalize">{{ people.status || '-' }}</CBadge>
                  <CBadge :color="requestStatusColor(request.status_label)">{{ request.status_label || '-' }}</CBadge>
                </div>
              </div>
              <CRow>
                <CCol md="4" class="mb-2"><div class="text-muted small">Requested iCare</div>{{ request.icare_name || '-' }}</CCol>
                <CCol md="4" class="mb-2"><div class="text-muted small">iCL</div>{{ request.icl_name || '-' }}</CCol>
                <CCol md="4" class="mb-2"><div class="text-muted small">Request Date</div>{{ formatDateTime(request.created_at) }}</CCol>
                <CCol md="4" class="mb-2"><div class="text-muted small">Email</div>{{ people.email || '-' }}</CCol>
                <CCol md="4" class="mb-2"><div class="text-muted small">Phone</div>{{ people.mobile_phone || people.phone || '-' }}</CCol>
              </CRow>
            </div>
          </div>

          <!-- REQUEST INFO -->
          <div class="card">
            <div class="card-header"><strong>Request Info</strong></div>
            <div class="card-body">
              <CRow>
                <CCol md="6" class="mb-3"><div class="text-muted small">Reason to Join</div>{{ request.reason_request || '-' }}</CCol>
                <CCol md="6" class="mb-3"><div class="text-muted small">Heard About</div>{{ request.hear_about || '-' }}</CCol>
                <CCol v-if="request.status_label === 'Rejected'" md="6" class="mb-3">
                  <div class="text-muted small">Reject Reason</div>{{ request.reason || '-' }}
                </CCol>
                <CCol v-if="request.status_label === 'Cancelled'" md="6" class="mb-3">
                  <div class="text-muted small">Cancel Reason</div>{{ request.reason_cancel || '-' }}
                </CCol>
                <template v-if="request.status_label && request.status_label !== 'Pending'">
                  <CCol md="6" class="mb-3"><div class="text-muted small">Processed By</div>{{ request.updated_by || '-' }}</CCol>
                  <CCol md="6" class="mb-3"><div class="text-muted small">Processed At</div>{{ formatDateTime(request.updated_at) }}</CCol>
                </template>
              </CRow>
            </div>
          </div>

          <!-- LOG JOURNEY -->
          <div class="card">
            <div class="card-header"><strong>Log Journey</strong></div>
            <div class="card-body">
              <div v-if="journeyLogs.length > 0" class="journey-log-list">
                <div v-for="entry in journeyLogs" :key="entry.id" class="journey-log-item">
                  <div class="d-flex justify-content-between align-items-start flex-wrap">
                    <div class="mb-1">
                      <CBadge :color="journeyBadgeColor(entry)">{{ journeyLabel(entry) }}</CBadge>
                    </div>
                    <div class="text-right">
                      <small class="text-muted d-block">{{ formatDateTime(entry.checked_at || entry.created_at) }}</small>
                      <small class="text-muted d-block">{{ entry.checked_by_name ? `By ${entry.checked_by_name}` : 'By -' }}</small>
                    </div>
                  </div>
                  <div class="journey-log-value text-muted">{{ journeyValue(entry) }}</div>
                </div>
              </div>
              <div v-else class="text-muted small">No journey yet</div>
            </div>
          </div>

          <!-- PERSONAL INFORMATION -->
          <div class="card">
            <div class="card-header"><strong>Personal Information</strong></div>
            <div class="card-body">
              <CRow>
                <CCol md="6" class="mb-2"><div class="text-muted small">Date of Birth</div>{{ people.dob || '-' }}</CCol>
                <CCol md="6" class="mb-2"><div class="text-muted small">Sex</div>{{ people.sex || '-' }}</CCol>
                <CCol md="6" class="mb-2"><div class="text-muted small">Address 1</div>{{ people.address1 || '-' }}</CCol>
                <CCol md="6" class="mb-2"><div class="text-muted small">Address 2</div>{{ people.address2 || '-' }}</CCol>
                <CCol md="6" class="mb-2"><div class="text-muted small">City</div>{{ people.city || '-' }}</CCol>
                <CCol md="6" class="mb-2"><div class="text-muted small">State</div>{{ people.state || '-' }}</CCol>
                <CCol md="6" class="mb-2"><div class="text-muted small">Country</div>{{ people.country || '-' }}</CCol>
                <CCol md="6" class="mb-2"><div class="text-muted small">Zipcode</div>{{ people.zipcode || '-' }}</CCol>
              </CRow>
            </div>
          </div>
        </template>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import icare from '@/services/icare'

const REQUEST_STATUS_COLORS = {
  Pending: 'warning',
  Accepted: 'success',
  Rejected: 'danger',
  Cancelled: 'secondary',
  Done: 'secondary'
}

export default {
  name: 'IcareJoinDetail',
  data () {
    return {
      isLoading: false,
      loadError: '',
      request: {},
      people: {},
      journeyLogs: []
    }
  },
  computed: {
    initials () {
      const name = String(this.people.fullname || '').trim()
      if (!name) return '-'
      return name.split(/\s+/).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('')
    }
  },
  mounted () {
    this.loadData(this.$route.params.id)
  },
  watch: {
    '$route.params.id': function (newId) {
      this.loadData(newId)
    }
  },
  methods: {
    loadData (id) {
      if (!id) {
        return
      }

      this.request = {}
      this.people = {}
      this.journeyLogs = []
      this.loadError = ''
      this.isLoading = true
      icare.getJoinDetail(id)
        .then((res) => {
          const data = res.data || {}
          this.request = data.request || {}
          this.people = data.people || {}
          this.journeyLogs = Array.isArray(data.journey_logs) ? data.journey_logs : []
        })
        .catch((err) => {
          this.loadError = (err.response && err.response.data && err.response.data.message) || 'Failed to load detail data'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    requestStatusColor (label) {
      return REQUEST_STATUS_COLORS[label] || 'secondary'
    },
    peopleStatusColor (status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized === 'member') return 'success'
      if (normalized === 'visitor') return 'warning'
      if (normalized === 'newcomer') return 'info'
      return 'secondary'
    },
    journeyLabel (entry) {
      if (entry && (entry.source === 'people_log' || entry.source === 'icare_join')) return entry.label || 'iCare Log'
      const status = String((entry && entry.value_json && entry.value_json.status) || '').toLowerCase()
      if (status === 'in_progress') return 'In Progress'
      if (status === 'close') {
        const closeType = String((entry && entry.value_json && entry.value_json.close_type) || '').toLowerCase()
        if (closeType === 'assign_icare') return 'Close - Assign iCare'
        if (closeType === 'reason') return 'Close - Reason'
        return 'Close'
      }
      if (status === 'open') return 'Open'
      return 'Follow Up'
    },
    journeyBadgeColor (entry) {
      if (entry && entry.source === 'people_log') {
        if (entry.action === 'promote') return 'info'
        if (entry.action === 'demote') return 'secondary'
        if (entry.action === 'move') return 'primary'
        return 'dark'
      }
      if (entry && entry.source === 'icare_join') {
        const id = String(entry.id || '')
        if (id.endsWith('-accepted')) return 'success'
        if (id.endsWith('-rejected')) return 'danger'
        if (id.endsWith('-cancelled') || id.endsWith('-done')) return 'secondary'
        return 'primary'
      }
      const status = String((entry && entry.value_json && entry.value_json.status) || '').toLowerCase()
      if (status === 'close') {
        const closeType = String((entry && entry.value_json && entry.value_json.close_type) || '').toLowerCase()
        return closeType === 'reason' ? 'warning' : 'success'
      }
      if (status === 'in_progress') return 'info'
      return 'secondary'
    },
    journeyValue (entry) {
      if (!entry) return '-'
      const valueJson = entry.value_json || {}
      if (valueJson.reason) return valueJson.reason
      if (valueJson.close_value) return valueJson.close_value
      if (entry.value_text) return entry.value_text
      return '-'
    },
    formatDateTime (value) {
      if (!value) return '-'
      const date = new Date(String(value).replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString()
    },
    goBack () {
      this.$router.push({ path: '/icare/icare_join' })
    }
  }
}
</script>

<style scoped>
.join-photo {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
}
.join-photo-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e4e9f2;
  color: #3c4b64;
  font-weight: 600;
  font-size: 20px;
}
.journey-log-list {
  display: grid;
  gap: 10px;
}
.journey-log-item {
  padding: 10px 12px;
  border: 1px solid #e4e9f2;
  border-radius: 8px;
  background: #fff;
}
.journey-log-value {
  font-size: 12px;
  line-height: 1.45;
  margin-top: 4px;
  word-break: break-word;
}
</style>
